<template>
  <!-- Only project managers can share an attachment: the drawer used to open with no tab at
       all for everyone else, which showed an empty panel. -->
  <app-share-drawer v-if="isCurrentUserPm"
                    :model-value="pane.isOpen"
                    :title="$t('common.share')"
                    data-locator="signing-checklist-attachment-pane"
                    :loading="shareAttachmentPending" :confirm-disabled="shareDisabled"
                    @close="closePane" @confirm="shareAttachmentFile"
  >
    <template v-if="pane.isOpen && selectedDocument" #body>
      <div class="d-flex flex-column ga-6">
        <div class="d-flex flex-column align-start ga-2">
          <h4>{{ $t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.selectedDocument') }}</h4>
          <v-chip variant="outlined">
            <ClosdFildersIcon class="mx-2" :document="selectedDocument" size="22"/>
            <span class="SigningChecklistAttachmentShare-filename">
              {{selectedDocument.basename}}
            </span>
          </v-chip>
        </div>

        <app-tile-group
          :model-value="shareType"
          :options="shareTypeOptions"
          @update:model-value="onShareTypeChange"
        />

        <DocumentSharePicker v-if="shareType === SHARE_TYPE_OPTIONS.CUSTOM && groups" :force-pm-selection="true" />

        <v-divider />

        <app-toggle
          v-model="sendNotification"
          hide-details
          :label="$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.sendNotification')"
        />

        <VisualTextEditor ref="notifyMessageInput"
                          v-model:value="notifyMessage"
                          counter="footer"
                          :error-messages="notifyMessageErrors"
                          :max-length="notifyMessageMaxLength"
                          :placeholder="$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.optionalMessageWithCopy')"
        />
      </div>
    </template>
  </app-share-drawer>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { CLOSE_PANE, SHARE_ATTACHMENT_FILE } from '@/store/modules/signing-checklist/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'SigningChecklistPaneShareAttachment',
  components: { ClosdFildersIcon, DocumentSharePicker, VisualTextEditor },
  data () {
    return {
      notifyMessage: '',
      notifyMessageMaxLength: NOTIFY_MESSAGE_MAX_LENGTH,
      sendNotification: true,
      SHARE_TYPE_OPTIONS,
      shareTypeValue: '',
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapState('groups', ['groups']),
    ...mapState('signingChecklist', ['shareAttachmentPending', 'pane']),
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    notifyMessageIsTooLong () {
      return this.notifyMessageLength > this.notifyMessageMaxLength
    },
    notifyMessageErrors () {
      const errors = []

      if (this.notifyMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    selectedDocument () {
      if (!this.pane?.fileToShare) {
        return null
      }

      const splicedFilename = this.pane.fileToShare.name.split('.')
      const fileExt = splicedFilename.pop()

      return {
        basename: splicedFilename.join('.'),
        ext: fileExt,
      }
    },
    shareDisabled () {
      return this.notifyMessageIsTooLong
    },
    shareTypeOptions () {
      return [
        {
          label: this.$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.publicTitle'),
          value: SHARE_TYPE_OPTIONS.PUBLIC,
          dataLocator: `${SHARE_TYPE_OPTIONS.PUBLIC}-share-option`,
        },
        {
          label: this.$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.customTitle'),
          value: SHARE_TYPE_OPTIONS.CUSTOM,
          dataLocator: `${SHARE_TYPE_OPTIONS.CUSTOM}-share-option`,
        },
      ]
    },
    shareType: {
      get () {
        if (this.shareTypeValue) {
          return this.shareTypeValue
        }

        if (this.pane.fileToShare) {
          return this.pane.fileToShare.rights.read.isPublic
            ? SHARE_TYPE_OPTIONS.PUBLIC
            : SHARE_TYPE_OPTIONS.CUSTOM
        }

        return SHARE_TYPE_OPTIONS.CUSTOM
      },
      set (val) {
        this.shareTypeValue = val
        if (val === SHARE_TYPE_OPTIONS.CUSTOM) {
          this.INIT_SELECTED_GROUPS(this.pane.fileToShare.rights.read.groups)
          this.INIT_SELECTED_MEMBERS(this.pane.fileToShare.rights.read.users)
        }
      },
    },
  },
  watch: {
    'pane.isOpen' (isOpen) {
      if (isOpen) {
        this.sendNotification = true
        this.notifyMessage = ''

        if (this.pane.fileToShare?.rights) {
          this.INIT_SELECTED_GROUPS(this.pane.fileToShare.rights.read.groups)
          this.INIT_SELECTED_MEMBERS(this.pane.fileToShare.rights.read.users)
        }

        if (this.$refs.notifyMessageInput) {
          this.$refs.notifyMessageInput.clear()
        }
      }
    },
  },
  methods: {
    ...mapActions('signingChecklist', [CLOSE_PANE, SHARE_ATTACHMENT_FILE]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    closePane () {
      this.CLOSE_PANE()
    },
    onShareTypeChange(value) {
      this.shareTypeValue = value
      if (value === SHARE_TYPE_OPTIONS.CUSTOM) {
        this.INIT_SELECTED_GROUPS(this.pane.fileToShare.rights.read.groups)
        this.INIT_SELECTED_MEMBERS(this.pane.fileToShare.rights.read.users)
      }
    },
    async shareAttachmentFile () {
      try {
        await this.SHARE_ATTACHMENT_FILE({
          notifyMessage: this.notifyMessage,
          sendNotification: this.sendNotification,
          shareType: this.shareType,
        })

        this.closePane()
        toast.success(this.$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.shareAttachmentFileSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.shareAttachmentFileError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.attachment-share-radio {
  // Deliberate exception: 7px selection band has no matching border-width token (only 0/1/2px
  // exist) and rounding to 2px would visibly thin it without design validation (AB#3108019).
  border-left: 7px solid transparent;
  // Bleeds through the drawer body padding so the highlighted band spans the full panel width,
  // as it did through the 20px padding of the pane this drawer replaced.
  margin-left: -24px;
  margin-right: -24px;
  padding-top: 14px;
  padding-bottom: 14px;

  &--active {
    border-left-color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-primary-lighten-4));
  }
}

.SigningChecklistAttachmentShare {
  &-filename {
    max-width: 350px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>
